import { createMemo } from "solid-js"
import { isPlainObjListDeeplyEqual } from "../../../isPlainObjListDeeplyEqual"
import isNotNil from "../../../ldsh/isNotNil"
import { immutableEmptyArray } from "../../../lib"
import eightDirectionCharsLowerCase, { type EightDirectionIndex } from "../../direction/eightDirectionCharsLowerCase"
import getOppositeDirectionIndex from "../../direction/getOppositeDirectionIndex"
import type { EntityElevation } from "../../entity/EntityElevation/EntityElevation.type"
import type { TileGraphCorner } from "./TileGraphCorner.type"
import getEightDirectionIndexFrom from "./getEightDirectionIndexFrom"

/**
 * Types of corner ridges that can be created between tiles.
 * - None: No ridge should be created (neither inset nor outset)
 * - Inset: A ridge that goes inward (lower elevation on the inside)
 * - Outset: A ridge that goes outward (higher elevation on the inside)
 * - Straight: A ridge along a direct edge between two tiles at the same elevation
 */
export enum CornerRidgeType {
  None = 0,
  Inset = 1,
  Outset = 2,
  Straight = 3,
}

/**
 * Represents a ridge at a tile corner in one of the 8 cardinal/intercardinal directions.
 * 
 * @property {EightDirectionIndex} edi - The direction index (0-7) of the ridge
 * @property {EntityElevation} min - The minimum elevation at this ridge
 * @property {EntityElevation} max - The maximum elevation at this ridge
 * @property {CornerRidgeType} type - Whether the ridge is inset or outset
 */
export type CornerRidge = {
  edi: EightDirectionIndex
  min: EntityElevation
  max: EntityElevation
  type: CornerRidgeType
}

/**
 * Creates a memoized SolidJS signal that computes the list of corner ridges for a tile graph corner.
 * The ridges are recalculated only when the underlying node data changes.
 * 
 * Each array is a stack of ridges, ordered from highest to lowest elevation.
 * A corner is surrounded by 3 TileGraphNodes and 4 TileGraphEdges.
 * Sometimes there will be 0-1 corner ridges,
 * however more complicated corners are possible,
 * when the surrounding 4 nodes are not all at the same elevation.
 * 
 * @param {TileGraphCorner['node']} nodeByLcMap - Map of nodes by location code
 * @param {TileGraphCorner['node']['desc']} getNodesDesc - Function to get node descriptions
 * @returns {Array<CornerRidge>} Memoized array of corner ridges
 * 
 * @example
 * const ridges = createCornerEightDirectionRidgesListMemo(nodeMap, getNodeDesc);
 */
export default function createCornerEightDirectionRidgesListMemo(
  nodeByLcMap: TileGraphCorner['node'],
  getNodesDesc: TileGraphCorner['node']['desc'],
) {
  // eslint-disable-next-line solid/reactivity
  return createMemo((): Array<CornerRidge> => {
    const [node0, node1, node2, node3] = getNodesDesc()

    // Group nodes by elevation in descending order
    const nodesByElevation = [node0, node1, node2, node3]
      .filter(isNotNil)
      .sort((a, b) => b.floor_e() - a.floor_e())

    // If all nodes have the same elevation, no ridges needed
    if (nodesByElevation.length === 0 || 
        nodesByElevation[0].floor_e() === nodesByElevation[nodesByElevation.length - 1].floor_e()) {
      return immutableEmptyArray as unknown as Array<CornerRidge>
    }

    const result: Array<CornerRidge> = []

    // Process elevation changes from highest to lowest
    for (let i = 0; i < nodesByElevation.length - 1; i++) {
      const currentElevation = nodesByElevation[i].floor_e()
      const nextElevation = nodesByElevation[i + 1]?.floor_e()
      
      // Skip if next node has the same elevation
      if (currentElevation === nextElevation) continue

      // Get all nodes at current elevation level
      const currentLevelNodes = nodesByElevation.filter(n => n.floor_e() === currentElevation)
      
      // Handle different node count cases
      if (currentLevelNodes.length === 1) {
        // Single highest node
        const node = currentLevelNodes[0]
        const edi = getEightDirectionIndexFrom(nodeByLcMap, node)
        if (isNotNil(edi)) {
          result.push({
            edi: edi as EightDirectionIndex,
            min: nextElevation,
            max: currentElevation,
            type: CornerRidgeType.Outset
          })
        }
      } else if (currentLevelNodes.length === 2) {
        // Two nodes at same elevation
        const edi0 = getEightDirectionIndexFrom(nodeByLcMap, currentLevelNodes[0])
        const edi1 = getEightDirectionIndexFrom(nodeByLcMap, currentLevelNodes[1])
        
        if (isNotNil(edi0) && isNotNil(edi1)) {
          const diff = Math.abs(edi0 - edi1)
          
          if (diff === 2) {
            // Nodes are opposite each other
            const mutualIndex = ((edi0 + edi1) / 2) as EightDirectionIndex
            result.push({
              edi: mutualIndex,
              min: nextElevation,
              max: currentElevation,
              type: CornerRidgeType.Outset
            })
          } else if (diff === 6) {
            // Handle wrap-around case (0 and 6)
            result.push({
              edi: 0, // or 4, depending on your coordinate system
              min: nextElevation,
              max: currentElevation,
              type: CornerRidgeType.Outset
            })
          } else {
            // Adjacent nodes, use opposite of first node
            const oppositeEdi = getOppositeDirectionIndex(eightDirectionCharsLowerCase, edi0)
            result.push({
              edi: oppositeEdi as EightDirectionIndex,
              min: nextElevation,
              max: currentElevation,
              type: CornerRidgeType.Outset
            })
          }
        }
      } else if (currentLevelNodes.length === 3) {
        // Three nodes at same elevation, ridge points to the fourth
        const allIndices = [0, 1, 2, 3]
        const missingNodeIndex = allIndices.find(i => 
          !currentLevelNodes.some(n => getEightDirectionIndexFrom(nodeByLcMap, n) === i)
        )
        
        if (isNotNil(missingNodeIndex)) {
          const oppositeEdi = getOppositeDirectionIndex(
            eightDirectionCharsLowerCase, 
            missingNodeIndex as EightDirectionIndex
          )
          
          result.push({
            edi: oppositeEdi as EightDirectionIndex,
            min: nextElevation,
            max: currentElevation,
            type: CornerRidgeType.Inset
          })
        }
      }
    }

    return result
  }, immutableEmptyArray as unknown as Array<CornerRidge>, {
    equals: isPlainObjListDeeplyEqual
  })
}